import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import Modal from '../components/Modal';
import { hinweis } from '../components/Hinweise';

const BEZAHL_LABELS = {
  ausstehend: { label: 'Ausstehend', cls: 'status-badge status-warning' },
  bezahlt:    { label: 'Bezahlt',    cls: 'status-badge status-aktiv' },
  erstattet:  { label: 'Erstattet', cls: 'status-badge status-inaktiv' },
};

const STATUS_LABELS = {
  neu:        { label: 'Neu',        cls: 'status-badge status-warning' },
  bestaetigt: { label: 'Bestätigt',  cls: 'status-badge status-aktiv' },
  storniert:  { label: 'Storniert',  cls: 'status-badge status-danger' },
};

function formatDatum(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatZeit(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function AngebotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [angebot, setAngebot] = useState(null);
  const [anmeldungen, setAnmeldungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('angemeldet'); // 'angemeldet' | 'warteliste'
  const [selected, setSelected] = useState(null);
  const [editAnm, setEditAnm] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [ra, ranm] = await Promise.all([
        api.get(`/api/admin/angebote/${id}`),
        api.get(`/api/admin/angebote/${id}/anmeldungen`),
      ]);
      setAngebot(ra.data);
      setAnmeldungen(ranm.data);
    } catch { setError('Angebot konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const angemeldet = anmeldungen.filter(a => !a.auf_warteliste && a.status !== 'storniert');
  const warteliste = anmeldungen.filter(a => a.auf_warteliste);
  const storniert  = anmeldungen.filter(a => a.status === 'storniert');

  const openEdit = (a) => {
    setEditAnm({ ...a });
    setSelected(a);
  };

  const handleSaveAnmeldung = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/angebote/${id}/anmeldungen/${editAnm.id}`, {
        bezahlstatus: editAnm.bezahlstatus,
        bezahlt_notiz: editAnm.bezahlt_notiz,
        teilnahme_berechtigt: editAnm.teilnahme_berechtigt,
        status: editAnm.status,
        bemerkung_intern: editAnm.bemerkung_intern,
      });
      await load();
      setSelected(null);
      setEditAnm(null);
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler'); }
    finally { setSaving(false); }
  };

  const handleZahlungBestaetigen = async (a) => {
    setSaving(true);
    try {
      await api.put(`/api/admin/angebote/${id}/anmeldungen/${a.id}`, {
        bezahlstatus: 'bezahlt',
        teilnahme_berechtigt: true,
      });
      await load();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Anmeldung von ${a.kontakt_vorname} ${a.kontakt_nachname} wirklich löschen?`)) return;
    try {
      await api.delete(`/api/admin/angebote/${id}/anmeldungen/${a.id}`);
      await load();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Fehler'); }
  };

  const openEmailVorschau = async (a) => {
    try {
      const r = await api.get(`/api/admin/angebote/${id}/anmeldungen/${a.id}/email-vorschau`);
      setEmailData(r.data);
      setEmailModal(a);
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Keine Vorlage verfügbar'); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  if (!angebot) return <div className="alert alert-error">{error}</div>;

  const belegungPct = angebot.max_teilnehmer ? (angemeldet.length / angebot.max_teilnehmer) * 100 : 0;

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <button className="btn btn-secondary btn-sm" style={{ marginBottom: '0.5rem' }} onClick={() => navigate('/angebote')}>← Zurück</button>
          <h2 className="page-title" style={{ marginBottom: 0 }}>{angebot.titel}</h2>
          {angebot.typ_name && (
            <span style={{
              fontSize: '0.78rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, marginTop: 4, display: 'inline-block',
              background: (angebot.typ_farbe || '#0099cc') + '22', color: angebot.typ_farbe || '#0099cc',
            }}>{angebot.typ_icon} {angebot.typ_name}</span>
          )}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/angebote/${id}/bearbeiten`)}>Bearbeiten</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Übersicht */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Anmeldungen', value: `${angemeldet.length}${angebot.max_teilnehmer ? ` / ${angebot.max_teilnehmer}` : ''}`, color: 'var(--primary)' },
          { label: 'Warteliste', value: warteliste.length, color: '#e65100' },
          { label: 'Bezahlt', value: angemeldet.filter(a => a.bezahlstatus === 'bezahlt').length, color: '#2e7d32' },
          { label: 'Berechtigt', value: angemeldet.filter(a => a.teilnahme_berechtigt).length, color: '#2e7d32' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '0.875rem 1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {angebot.max_teilnehmer && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ height: 6, background: '#e0e0e0', borderRadius: 3 }}>
            <div style={{
              height: '100%', borderRadius: 3, transition: 'width 0.3s',
              background: belegungPct >= 100 ? '#c62828' : belegungPct >= 80 ? '#e65100' : '#2e7d32',
              width: `${Math.min(belegungPct, 100)}%`,
            }} />
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
            Belegung: {Math.round(belegungPct)}%
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', borderBottom: '2px solid var(--border)' }}>
        {[
          { key: 'angemeldet', label: `Angemeldet (${angemeldet.length})` },
          { key: 'warteliste', label: `Warteliste (${warteliste.length})` },
          { key: 'storniert',  label: `Storniert (${storniert.length})` },
        ].map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            style={{
              padding: '0.6rem 1.2rem', border: 'none', background: 'transparent', cursor: 'pointer',
              fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -2, fontSize: '0.9rem',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Anmeldungs-Tabelle */}
      {(() => {
        const rows = tab === 'angemeldet' ? angemeldet : tab === 'warteliste' ? warteliste : storniert;
        if (rows.length === 0) return (
          <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
            Keine Einträge
          </div>
        );
        return (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    {tab === 'warteliste' && <th>#</th>}
                    <th>Name</th>
                    <th>E-Mail</th>
                    <th>Mitglied</th>
                    <th>Bezahlung</th>
                    <th>Berechtigt</th>
                    <th>Angemeldet am</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(a => {
                    const bl = BEZAHL_LABELS[a.bezahlstatus] || BEZAHL_LABELS.ausstehend;
                    const teiln = a.teilnehmer_vorname
                      ? `${a.kontakt_vorname} ${a.kontakt_nachname} → ${a.teilnehmer_vorname} ${a.teilnehmer_nachname}`
                      : `${a.kontakt_vorname} ${a.kontakt_nachname}`;
                    return (
                      <tr key={a.id}>
                        {tab === 'warteliste' && <td style={{ fontWeight: 700, color: '#e65100' }}>{a.warteliste_position}</td>}
                        <td>
                          <div style={{ fontWeight: 600 }}>{teiln}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{a.kontakt_telefon}</div>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{a.kontakt_email}</td>
                        <td>{a.ist_mitglied ? '✓' : '–'}</td>
                        <td>
                          <span className={bl.cls}>{bl.label}</span>
                          {a.bezahlt_am && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{formatDatum(a.bezahlt_am)} {formatZeit(a.bezahlt_am)}</div>}
                        </td>
                        <td>
                          {a.teilnahme_berechtigt
                            ? <span style={{ color: '#2e7d32', fontWeight: 700 }}>✓ Ja</span>
                            : <span style={{ color: '#c62828' }}>✗ Nein</span>}
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{formatDatum(a.created_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                            {a.bezahlstatus !== 'bezahlt' && tab !== 'storniert' && (
                              <button className="btn btn-success btn-sm" onClick={() => handleZahlungBestaetigen(a)}>
                                💳 Bezahlt
                              </button>
                            )}
                            <button className="btn btn-secondary btn-sm" onClick={() => openEmailVorschau(a)}>✉</button>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}>✏</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Edit-Modal */}
      {selected && editAnm && (
        <Modal title={`Anmeldung: ${selected.kontakt_vorname} ${selected.kontakt_nachname}`} onClose={() => { setSelected(null); setEditAnm(null); }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Bezahlstatus</label>
              <select className="form-control" value={editAnm.bezahlstatus}
                onChange={e => setEditAnm(p => ({ ...p, bezahlstatus: e.target.value }))}>
                <option value="ausstehend">Ausstehend</option>
                <option value="bezahlt">Bezahlt</option>
                <option value="erstattet">Erstattet</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={editAnm.status}
                onChange={e => setEditAnm(p => ({ ...p, status: e.target.value }))}>
                <option value="neu">Neu</option>
                <option value="bestaetigt">Bestätigt</option>
                <option value="storniert">Storniert</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Zahlungsnotiz</label>
            <input className="form-control" value={editAnm.bezahlt_notiz || ''}
              onChange={e => setEditAnm(p => ({ ...p, bezahlt_notiz: e.target.value }))}
              placeholder="z.B. Überweisung eingegangen am …" />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" id="tb" checked={editAnm.teilnahme_berechtigt}
              onChange={e => setEditAnm(p => ({ ...p, teilnahme_berechtigt: e.target.checked }))}
              style={{ width: 16, height: 16 }} />
            <label htmlFor="tb" style={{ margin: 0, cursor: 'pointer' }}>Zur Teilnahme berechtigt</label>
          </div>
          <div className="form-group">
            <label className="form-label">Interne Bemerkung</label>
            <textarea className="form-control" rows={3} value={editAnm.bemerkung_intern || ''}
              onChange={e => setEditAnm(p => ({ ...p, bemerkung_intern: e.target.value }))} />
          </div>

          {/* Zusatzfelder anzeigen */}
          {editAnm.zusatzfelder && Object.keys(editAnm.zusatzfelder).length > 0 && (
            <div style={{ background: '#f8f9fa', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem' }}>
              <p style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Formularfelder:</p>
              {Object.entries(editAnm.zusatzfelder).map(([k, v]) => (
                <div key={k} style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                  <strong>{k}:</strong> {String(v)}
                </div>
              ))}
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(null); setEditAnm(null); }}>Abbrechen</button>
            <button className="btn btn-primary btn-sm" onClick={handleSaveAnmeldung} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </button>
          </div>
        </Modal>
      )}

      {/* E-Mail Vorschau Modal */}
      {emailModal && emailData && (
        <Modal title={`E-Mail-Vorschau: ${emailModal.kontakt_vorname} ${emailModal.kontakt_nachname}`} onClose={() => { setEmailModal(null); setEmailData(null); }} size="lg">
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Vorlage: </span>
            <strong style={{ fontSize: '0.9rem' }}>{emailData.vorlage_name}</strong>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Betreff</label>
            <div style={{ background: '#f8f9fa', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.9rem', fontWeight: 600 }}>
              {emailData.betreff}
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Vorschau (HTML)</label>
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
              <iframe
                srcDoc={emailData.html}
                style={{ width: '100%', height: 350, border: 'none' }}
                title="E-Mail-Vorschau"
              />
            </div>
          </div>
          <div className="alert" style={{ background: '#fff8e1', borderColor: '#ffe082', color: '#5d4037', fontSize: '0.85rem' }}>
            ℹ️ Diese Vorschau zeigt wie die E-Mail aussehen würde. Der Versand erfolgt manuell (z.B. per Copy-Paste in dein E-Mail-Programm).
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => { setEmailModal(null); setEmailData(null); }}>Schließen</button>
            <button className="btn btn-primary btn-sm" onClick={() => {
              navigator.clipboard?.writeText(emailData.text || '');
              hinweis.fehler('Text-Version in Zwischenablage kopiert');
            }}>Text kopieren</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
